// Generic table for backoffice list views. Each column declares its own
// `cell` renderer so the table doesn't need to know about per-cell
// formatting (status pills, currency, avatar+name composites).

import type { ReactNode } from 'react';

export interface DataTableColumn<Row> {
  key: string;
  /** Header label (uppercase tracked text). */
  h: string;
  /** Render fn for the cell. Falls back to the row's value at `key`. */
  cell?: (row: Row) => ReactNode;
  /** Right-align numeric columns. */
  right?: boolean;
  /** Render in JetBrains-mono. */
  mono?: boolean;
  /** Fixed column width (e.g. '120px' or '8rem'). */
  w?: string;
}

export type RowKey = string | number;

/**
 * Optional multi-select state. When provided, the table prepends a
 * checkbox column and the row click handler is suppressed on the
 * checkbox cell so toggling a selection doesn't also navigate. The
 * caller owns the Set; the table is purely controlled.
 */
export interface DataTableSelection<Row> {
  selectedIds: Set<RowKey>;
  onToggle: (id: RowKey, row: Row) => void;
  /** Receives the full id list currently rendered — for select-all. */
  onToggleAll: (ids: RowKey[]) => void;
}

/**
 * Server-side pagination state. The table renders a footer pager and the
 * caller owns page state + refetching (page goes into the query key).
 * `total` is the full filtered row count from the API, not rows.length.
 */
export interface DataTablePagination {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  /**
   * Lets the reader pick rows-per-page. When given, the pager shows a
   * selector (see `limitOptions`); the caller resets `page` to 1 on change.
   */
  onLimitChange?: (limit: number) => void;
  /** Choices for the selector. Defaults to 25 / 50 / 100. */
  limitOptions?: number[];
}

export const DEFAULT_LIMIT_OPTIONS = [25, 50, 100];

export interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  onRowClick?: (row: Row) => void;
  dense?: boolean;
  getKey?: (row: Row, i: number) => RowKey;
  emptyLabel?: string;
  selection?: DataTableSelection<Row>;
  pagination?: DataTablePagination;
}

/**
 * The footer pager, exported so a list that renders its own table (Drivers)
 * paginates with the same control instead of a hand-rolled Prev/Next.
 */
export function TablePager({
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  limitOptions = DEFAULT_LIMIT_OPTIONS,
}: DataTablePagination) {
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const btn =
    'h-7 rounded-md border border-[color:var(--bo-line)] bg-white px-2.5 text-[12px] font-medium text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bo-accent)] disabled:cursor-default disabled:opacity-40 disabled:hover:border-[color:var(--bo-line)]';
  return (
    <div className="flex items-center justify-between gap-3 border-t border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] px-3 py-2">
      <span className="flex items-center gap-3 text-[12px] tabular-nums text-[color:var(--bo-mute)]">
        <span>
          {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
        </span>
        {onLimitChange ? (
          <label className="flex items-center gap-1.5">
            <span>Rows per page</span>
            <select
              aria-label="Rows per page"
              className="h-7 rounded-md border border-[color:var(--bo-line)] bg-white px-1.5 text-[12px] text-[color:var(--bo-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bo-accent)]"
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
            >
              {(limitOptions.includes(limit) ? limitOptions : [...limitOptions, limit].sort((a, b) => a - b)).map(
                (n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ),
              )}
            </select>
          </label>
        ) : null}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={btn}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          ‹ Prev
        </button>
        <span className="text-[12px] tabular-nums text-[color:var(--bo-mute)]">
          Page {page.toLocaleString()} of {pageCount.toLocaleString()}
        </span>
        <button
          type="button"
          className={btn}
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        >
          Next ›
        </button>
      </div>
    </div>
  );
}

export function DataTable<Row extends object>({
  columns,
  rows,
  onRowClick,
  dense = false,
  getKey = (r, i) => (r as { id?: string | number }).id ?? i,
  emptyLabel = 'No matching records.',
  selection,
  pagination,
}: DataTableProps<Row>) {
  // Header checkbox tri-state: none → all → none. When some but not all
  // rows are selected we show an indeterminate checkbox.
  const visibleIds = rows.map((r, i) => getKey(r, i));
  const selectedVisible = visibleIds.filter((id) => selection?.selectedIds.has(id));
  const allSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;
  const someSelected = selectedVisible.length > 0 && !allSelected;

  return (
    <div className="overflow-hidden rounded-xl border border-[color:var(--bo-line)] bg-white">
      <div className="scroll-x overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-[color:var(--bo-line)] bg-[color:var(--bo-bg)]">
              {selection ? (
                <th
                  scope="col"
                  className="w-[42px] px-3 py-2.5 text-left"
                  aria-label="Select all"
                >
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={() => selection.onToggleAll(visibleIds)}
                    className="h-3.5 w-3.5 cursor-pointer accent-[color:var(--bo-accent)]"
                  />
                </th>
              ) : null}
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--bo-mute)] ${c.right ? 'text-right' : ''}`}
                  style={{ width: c.w }}
                >
                  {c.h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const id = getKey(r, i);
              const isSelected = selection?.selectedIds.has(id) ?? false;
              return (
                <tr
                  key={id}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  className={`border-b border-[color:var(--bo-line)] last:border-0 ${
                    onRowClick ? 'cursor-pointer hover:bg-[color:var(--bo-bg)]' : ''
                  } ${isSelected ? 'bg-[color:var(--bo-bg)]/60' : ''}`}
                >
                  {selection ? (
                    <td
                      className="w-[42px] px-3 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => selection.onToggle(id, r)}
                        aria-label={`Select row ${id}`}
                        className="h-3.5 w-3.5 cursor-pointer accent-[color:var(--bo-accent)]"
                      />
                    </td>
                  ) : null}
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-3 ${dense ? 'py-2' : 'py-3'} ${c.right ? 'text-right' : ''} ${
                        c.mono ? 'font-mono text-[12px]' : ''
                      }`}
                    >
                      {c.cell
                        ? c.cell(r)
                        : ((r as unknown as Record<string, ReactNode>)[c.key] ?? null)}
                    </td>
                  ))}
                </tr>
              );
            })}
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selection ? 1 : 0)}
                  className="px-3 py-12 text-center text-[13px] text-[color:var(--bo-mute)]"
                >
                  {emptyLabel}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {/* Shown whenever there is anything to count, not only past the first
          page: a 47-row list with a 50-row page used to look unpaginated,
          and the rows-per-page choice has to live somewhere. */}
      {pagination && pagination.total > 0 ? <TablePager {...pagination} /> : null}
    </div>
  );
}
